import React, { useEffect, useState } from 'react';
import {
  PlusIcon,
  ChartBarIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  ActualsFilter,
  DemandForecast,
  ForecastMethod,
  ForecastPeriod,
  ForecastMethodLabels,
  ForecastPeriodLabels,
} from '../../types/demand-forecasting.types';
import {
  useDemandForecastsPage,
  useDemandForecastSummary,
  useCreateDemandForecast,
  useGenerateDemandForecasts,
  useUpdateDemandForecast,
  useDeleteDemandForecast,
  useRecordActualDemand,
} from '../../hooks/useDemandForecasting';
import DemandForecastForm, {
  DemandForecastSubmission,
  METHOD_HINTS,
  formatForecastDate,
} from '../forms/DemandForecastForm';
import { Modal } from '../ui/Modal';
import { Pagination } from '../ui/Pagination';
import {
  ConfirmDialog,
  DetailCard,
  DetailItem,
  Field,
  FormActions,
  badgeClass,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../ui/FormControls';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// Chart colours: past demand, the forecast and the actual result
const HISTORY_COLOR = '#94a3b8';
const FORECAST_COLOR = '#4f46e5';
const ACTUAL_COLOR = '#0d9488';

const formatQuantity = (value?: number | null) =>
  value === null || value === undefined ? '—' : formatNumber(value, 2);

const getAccuracyTone = (accuracy: number) => {
  if (accuracy >= 90) return 'text-green-700 bg-green-50 ring-green-600/20';
  if (accuracy >= 70) return 'text-yellow-800 bg-yellow-50 ring-yellow-600/20';
  return 'text-red-700 bg-red-50 ring-red-600/20';
};

const hasActual = (forecast: DemandForecast) =>
  forecast.actualDemand !== null && forecast.actualDemand !== undefined;

// The period is over (or has started) and nobody has recorded what was really sold
const isAwaitingActual = (forecast: DemandForecast) =>
  forecast.isActive && !hasActual(forecast) && new Date(forecast.forecastDate) <= new Date();

const describeParameters = (forecast: DemandForecast) => {
  if (forecast.method === ForecastMethod.MOVING_AVERAGE) return `Last ${forecast.periodLookback} periods`;
  if (forecast.method === ForecastMethod.SEASONAL_NAIVE) return `Season of ${forecast.periodLookback} periods`;
  if (forecast.method === ForecastMethod.EXPONENTIAL_SMOOTHING) {
    return `Smoothing factor ${forecast.smoothingFactor ?? 0.3}`;
  }
  return 'No parameters';
};

const DemandForecastingScreen: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState<ForecastMethod | ''>('');
  const [periodFilter, setPeriodFilter] = useState<ForecastPeriod | ''>('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | ''>('');
  const [actualsFilter, setActualsFilter] = useState<ActualsFilter | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultRowsPerPage);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingForecast, setEditingForecast] = useState<DemandForecast | null>(null);
  const [viewForecast, setViewForecast] = useState<DemandForecast | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DemandForecast | null>(null);
  const [actualTarget, setActualTarget] = useState<DemandForecast | null>(null);
  const [isActualOpen, setIsActualOpen] = useState(false);
  const [actualValue, setActualValue] = useState('');
  const [actualError, setActualError] = useState('');

  // Debounce search so the API is not called on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: forecastPage, isLoading, isFetching, error } = useDemandForecastsPage({
    page,
    limit: pageSize,
    search: debouncedSearch || undefined,
    method: methodFilter || undefined,
    period: periodFilter || undefined,
    isActive: statusFilter ? statusFilter === 'active' : undefined,
    actuals: actualsFilter || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });
  const { data: summary, isLoading: isSummaryLoading } = useDemandForecastSummary();
  const createMutation = useCreateDemandForecast();
  const generateMutation = useGenerateDemandForecasts();
  const updateMutation = useUpdateDemandForecast();
  const deleteMutation = useDeleteDemandForecast();
  const actualMutation = useRecordActualDemand();

  const forecasts = forecastPage?.data ?? [];
  const total = forecastPage?.total ?? 0;
  const totalPages = forecastPage?.totalPages ?? 1;
  const isSaving = createMutation.isPending || generateMutation.isPending || updateMutation.isPending;

  // Step back when the current page no longer exists
  useEffect(() => {
    if (forecastPage && page > 1 && page > forecastPage.totalPages) {
      setPage(Math.max(1, forecastPage.totalPages));
    }
  }, [forecastPage, page]);

  const openCreate = () => {
    setEditingForecast(null);
    setIsFormOpen(true);
  };

  const openEdit = (forecast: DemandForecast) => {
    setEditingForecast(forecast);
    setIsFormOpen(true);
  };

  const openView = (forecast: DemandForecast) => {
    setViewForecast(forecast);
    setIsViewOpen(true);
  };

  const openActual = (forecast: DemandForecast) => {
    setActualTarget(forecast);
    setActualValue(hasActual(forecast) ? String(forecast.actualDemand) : '');
    setActualError('');
    setIsActualOpen(true);
  };

  const handleSubmit = async (submission: DemandForecastSubmission) => {
    try {
      if (submission.kind === 'series') {
        const created = await generateMutation.mutateAsync(submission.data);
        toast.success(`${created.length} forecasts created successfully`);
        setPage(1);
      } else if (editingForecast) {
        await updateMutation.mutateAsync({ id: editingForecast.id, data: submission.data });
        toast.success('Forecast updated successfully');
      } else {
        await createMutation.mutateAsync(submission.data);
        toast.success('Forecast created successfully');
        setPage(1);
      }
      setIsFormOpen(false);
    } catch (err) {
      toast.error(
        getApiErrorMessage(err, editingForecast ? 'Could not update the forecast' : 'Could not create the forecast'),
      );
      throw err;
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success('Forecast deleted successfully');
      setDeleteTarget(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not delete the forecast'));
    }
  };

  const submitActual = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!actualTarget) return;

    const value = Number(actualValue);
    if (actualValue.trim() === '' || !Number.isFinite(value) || value < 0) {
      setActualError('Enter the actual demand as a number of 0 or more');
      return;
    }

    try {
      await actualMutation.mutateAsync({ id: actualTarget.id, actualDemand: value });
      toast.success('Actual demand recorded');
      setIsActualOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not record the actual demand'));
    }
  };

  const hasFilters = !!(
    search ||
    methodFilter ||
    periodFilter ||
    statusFilter ||
    actualsFilter ||
    dateFrom ||
    dateTo
  );

  const clearFilters = () => {
    setSearch('');
    setMethodFilter('');
    setPeriodFilter('');
    setStatusFilter('');
    setActualsFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const stats = [
    {
      label: 'Total Forecasts',
      value: formatNumber(summary?.total ?? 0),
      hint: `${formatNumber(summary?.withActuals ?? 0)} with actual demand`,
      icon: ChartBarIcon,
      tone: 'text-indigo-600 bg-indigo-50',
    },
    {
      label: 'Active',
      value: formatNumber(summary?.active ?? 0),
      hint: `${formatNumber((summary?.total ?? 0) - (summary?.active ?? 0))} inactive`,
      icon: CheckCircleIcon,
      tone: 'text-green-600 bg-green-50',
    },
    {
      label: 'Awaiting Actuals',
      value: formatNumber(summary?.awaitingActuals ?? 0),
      hint: 'Period started, nothing recorded',
      icon: ClockIcon,
      tone: 'text-yellow-600 bg-yellow-50',
    },
    {
      label: 'Average Accuracy',
      value: summary?.averageAccuracy == null ? '—' : `${formatNumber(summary.averageAccuracy, 1)}%`,
      hint:
        summary?.averageError == null
          ? 'No actual demand recorded yet'
          : `Average error ${formatNumber(summary.averageError, 2)} units`,
      icon: ArrowTrendingUpIcon,
      tone: 'text-teal-600 bg-teal-50',
    },
  ];

  const selectClass =
    'h-10 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';
  const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
  const iconButtonClass = 'p-1.5 rounded disabled:opacity-50';

  const renderStatus = (forecast: DemandForecast) => (
    <span
      className={`${badgeClass} ${
        forecast.isActive ? 'text-green-700 bg-green-50 ring-green-600/20' : 'text-gray-700 bg-gray-50 ring-gray-500/20'
      }`}
    >
      {forecast.isActive ? 'Active' : 'Inactive'}
    </span>
  );

  const renderAccuracy = (forecast: DemandForecast) =>
    forecast.accuracy === null || forecast.accuracy === undefined ? (
      <span className="text-gray-400">—</span>
    ) : (
      <span className={`${badgeClass} ${getAccuracyTone(forecast.accuracy)}`}>
        {formatNumber(forecast.accuracy, 1)}%
      </span>
    );

  // Bars for the details chart: the history, then the forecast and the actual result
  const chartData = viewForecast
    ? [
        ...(viewForecast.historicalData || []).map((value, index) => ({
          label: `P${index + 1}`,
          value: Number(value),
          color: HISTORY_COLOR,
        })),
        { label: 'Forecast', value: viewForecast.forecastedDemand, color: FORECAST_COLOR },
        ...(hasActual(viewForecast)
          ? [{ label: 'Actual', value: Number(viewForecast.actualDemand), color: ACTUAL_COLOR }]
          : []),
      ]
    : [];

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading demand forecasts</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Demand Forecasting</h1>
          <p className="mt-1 text-sm text-gray-600">
            Forecast product demand from past sales and track how accurate each forecast was
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          New Forecast
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="flex items-center gap-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200"
          >
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${stat.tone}`}>
              <stat.icon className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-500">{stat.label}</p>
              <p className="truncate text-xl font-semibold text-gray-900">{isSummaryLoading ? '—' : stat.value}</p>
              <p className="truncate text-xs text-gray-500">{isSummaryLoading ? ' ' : stat.hint}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        {/* Search & filters */}
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by product name or SKU..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block h-10 w-full rounded-md border border-gray-300 bg-white pl-10 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              aria-label="Filter by method"
              value={methodFilter}
              onChange={(e) => {
                setMethodFilter(e.target.value as ForecastMethod | '');
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">All methods</option>
              {Object.values(ForecastMethod).map((method) => (
                <option key={method} value={method}>
                  {ForecastMethodLabels[method]}
                </option>
              ))}
            </select>
            <select
              aria-label="Filter by period"
              value={periodFilter}
              onChange={(e) => {
                setPeriodFilter(e.target.value as ForecastPeriod | '');
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">All periods</option>
              {Object.values(ForecastPeriod).map((period) => (
                <option key={period} value={period}>
                  {ForecastPeriodLabels[period]}
                </option>
              ))}
            </select>
            <select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as 'active' | 'inactive' | '');
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select
              aria-label="Filter by actual demand"
              value={actualsFilter}
              onChange={(e) => {
                setActualsFilter(e.target.value as ActualsFilter | '');
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">With or without actuals</option>
              <option value="recorded">Actual recorded</option>
              <option value="pending">Actual not recorded</option>
            </select>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              Forecast date
              <input
                type="date"
                aria-label="Forecast date from"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
                className={selectClass}
              />
              to
              <input
                type="date"
                aria-label="Forecast date to"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
                className={selectClass}
              />
            </label>
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex h-10 items-center rounded-md px-2 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                <XMarkIcon className="h-4 w-4 mr-1" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Forecasts table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>Product</th>
                <th className={thClass}>Method</th>
                <th className={thClass}>Forecast Date</th>
                <th className={`${thClass} text-right`}>Forecast</th>
                <th className={`${thClass} text-right`}>Actual</th>
                <th className={thClass}>Accuracy</th>
                <th className={thClass}>Status</th>
                <th className={`${thClass} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y divide-gray-100 bg-white ${isFetching && !isLoading ? 'opacity-60' : ''}`}>
              {isLoading ? (
                <tr>
                  <td className="px-4 py-12 text-center text-sm text-gray-500" colSpan={8}>
                    Loading forecasts...
                  </td>
                </tr>
              ) : forecasts.length === 0 ? (
                <tr>
                  <td className="px-4 py-12 text-center" colSpan={8}>
                    <ChartBarIcon className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-2 text-sm font-medium text-gray-900">No forecasts found</p>
                    <p className="mt-1 text-sm text-gray-500">
                      {hasFilters
                        ? 'Try changing or clearing the search and filters.'
                        : 'Create a forecast to plan stock and production for a product.'}
                    </p>
                  </td>
                </tr>
              ) : (
                forecasts.map((forecast) => (
                  <tr key={forecast.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900">{forecast.productName}</div>
                      <div>
                        <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                          {forecast.productSku}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      <div className="text-gray-900">{ForecastMethodLabels[forecast.method]}</div>
                      <div className="text-xs text-gray-500">{ForecastPeriodLabels[forecast.period]}</div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      <div>{formatForecastDate(forecast.forecastDate)}</div>
                      {isAwaitingActual(forecast) && <div className="text-xs text-yellow-700">Actual due</div>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">
                      {formatQuantity(forecast.forecastedDemand)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">
                      {formatQuantity(forecast.actualDemand)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">{renderAccuracy(forecast)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">{renderStatus(forecast)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-1">
                        <button
                          onClick={() => openActual(forecast)}
                          className="inline-flex items-center rounded-md bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-700 hover:bg-teal-100"
                        >
                          <ClipboardDocumentCheckIcon className="h-3.5 w-3.5 mr-1" />
                          {hasActual(forecast) ? 'Change Actual' : 'Record Actual'}
                        </button>
                        <button
                          onClick={() => openView(forecast)}
                          className={`${iconButtonClass} text-gray-600 hover:bg-gray-100 hover:text-gray-900`}
                          title="View Details"
                          aria-label="View Details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openEdit(forecast)}
                          className={`${iconButtonClass} text-indigo-600 hover:bg-indigo-50 hover:text-indigo-900`}
                          title="Edit Forecast"
                          aria-label="Edit Forecast"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(forecast)}
                          className={`${iconButtonClass} text-red-600 hover:bg-red-50 hover:text-red-900`}
                          title="Delete Forecast"
                          aria-label="Delete Forecast"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && total > 0 && (
          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:gap-6">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
              <span>
                Showing {formatNumber((page - 1) * pageSize + 1)}–
                {formatNumber(Math.min(page * pageSize, total))} of {formatNumber(total)} forecasts
              </span>
              <label className="flex items-center gap-2">
                Rows per page
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <Pagination className="lg:flex-1" currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>

      {/* Create or edit */}
      <Modal
        isOpen={isFormOpen}
        onClose={() => !isSaving && setIsFormOpen(false)}
        title={editingForecast ? 'Edit Forecast' : 'New Forecast'}
        description={
          editingForecast
            ? `${editingForecast.productName} · ${formatForecastDate(editingForecast.forecastDate)}`
            : 'Calculate demand from past periods, or enter a forecast by hand.'
        }
        size="2xl"
        className="overflow-visible"
      >
        <DemandForecastForm
          key={editingForecast?.id || 'new'}
          forecast={editingForecast}
          onSubmit={handleSubmit}
          onCancel={() => setIsFormOpen(false)}
          isSaving={isSaving}
        />
      </Modal>

      {/* Details */}
      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Forecast Details" size="xl">
        {viewForecast && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p>
                  <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                    {viewForecast.productSku}
                  </span>
                </p>
                <h4 className="mt-2 text-lg font-semibold text-gray-900">{viewForecast.productName}</h4>
                <p className="mt-1 text-sm text-gray-600">
                  {ForecastPeriodLabels[viewForecast.period]} forecast for{' '}
                  {formatForecastDate(viewForecast.forecastDate)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {isAwaitingActual(viewForecast) && (
                  <span className={`${badgeClass} text-yellow-800 bg-yellow-50 ring-yellow-600/20`}>Actual due</span>
                )}
                {renderStatus(viewForecast)}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {[
                { label: 'Forecast', value: formatQuantity(viewForecast.forecastedDemand), tone: 'bg-indigo-50 text-indigo-900' },
                { label: 'Actual', value: formatQuantity(viewForecast.actualDemand), tone: 'bg-teal-50 text-teal-900' },
                { label: 'Error', value: formatQuantity(viewForecast.meanAbsoluteError), tone: 'bg-gray-50 text-gray-900' },
                {
                  label: 'Accuracy',
                  value: viewForecast.accuracy == null ? '—' : `${formatNumber(viewForecast.accuracy, 1)}%`,
                  tone: 'bg-gray-50 text-gray-900',
                },
              ].map((item) => (
                <div key={item.label} className={`rounded-lg px-4 py-3 ${item.tone}`}>
                  <p className="text-xs font-medium uppercase tracking-wide opacity-70">{item.label}</p>
                  <p className="mt-1 text-xl font-semibold tabular-nums">{item.value}</p>
                </div>
              ))}
            </div>

            <DetailCard title="How it was forecast" columns={4}>
              <DetailItem label="Method">{ForecastMethodLabels[viewForecast.method]}</DetailItem>
              <DetailItem label="Parameters">{describeParameters(viewForecast)}</DetailItem>
              <DetailItem label="History used">
                {viewForecast.historicalData?.length
                  ? `${viewForecast.historicalData.length} periods`
                  : 'None, entered by hand'}
              </DetailItem>
              <DetailItem label="Last updated">{formatForecastDate(viewForecast.updatedAt)}</DetailItem>
              <DetailItem label="About the method" className="col-span-2 lg:col-span-4">
                <span className="font-normal text-gray-600">{METHOD_HINTS[viewForecast.method]}</span>
              </DetailItem>
            </DetailCard>

            {(viewForecast.historicalData?.length ?? 0) > 0 && (
              <section className="rounded-lg border border-gray-200 bg-white">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-2.5">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Demand by period</h4>
                  <div className="flex items-center gap-4 text-xs text-gray-600">
                    {[
                      { label: 'History', color: HISTORY_COLOR },
                      { label: 'Forecast', color: FORECAST_COLOR },
                      ...(hasActual(viewForecast) ? [{ label: 'Actual', color: ACTUAL_COLOR }] : []),
                    ].map((item) => (
                      <span key={item.label} className="flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: item.color }} />
                        {item.label}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="h-56 p-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                      <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#6b7280' }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} tickLine={false} axisLine={false} width={48} />
                      <Tooltip
                        cursor={{ fill: '#f3f4f6' }}
                        formatter={(value: number) => [formatNumber(value, 2), 'Demand']}
                      />
                      <Bar dataKey="value" radius={[3, 3, 0, 0]} maxBarSize={40} isAnimationActive={false}>
                        {chartData.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </section>
            )}

            {viewForecast.notes && (
              <DetailCard title="Notes" columns={2}>
                <dd className="col-span-2 whitespace-pre-line text-sm text-gray-700">{viewForecast.notes}</dd>
              </DetailCard>
            )}
          </div>
        )}

        <FormActions>
          <button type="button" onClick={() => setIsViewOpen(false)} className={secondaryButtonClass}>
            Close
          </button>
          {viewForecast && (
            <button
              type="button"
              onClick={() => {
                setIsViewOpen(false);
                openEdit(viewForecast);
              }}
              className={primaryButtonClass}
            >
              <PencilIcon className="mr-2 h-4 w-4" />
              Edit Forecast
            </button>
          )}
        </FormActions>
      </Modal>

      {/* Record actual demand */}
      <Modal
        isOpen={isActualOpen}
        onClose={() => !actualMutation.isPending && setIsActualOpen(false)}
        title="Record Actual Demand"
        description={
          actualTarget
            ? `${actualTarget.productName} · ${formatForecastDate(actualTarget.forecastDate)}`
            : undefined
        }
        size="sm"
      >
        {actualTarget && (
          <form onSubmit={submitActual} noValidate>
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-md bg-indigo-50 px-3 py-2 text-sm">
                <span className="text-indigo-700">Forecasted demand</span>
                <span className="font-semibold text-indigo-900 tabular-nums">
                  {formatQuantity(actualTarget.forecastedDemand)}
                </span>
              </div>
              <Field
                label="Actual Demand"
                htmlFor="actual-demand"
                required
                error={actualError}
                hint="What was really sold in this period. The accuracy is worked out from it."
              >
                <input
                  id="actual-demand"
                  type="number"
                  min="0"
                  step="0.01"
                  autoFocus
                  value={actualValue}
                  onChange={(e) => {
                    setActualValue(e.target.value);
                    setActualError('');
                  }}
                  className={cn(inputClass, actualError && inputErrorClass)}
                  placeholder="0"
                />
              </Field>
            </div>
            <FormActions>
              <button
                type="button"
                onClick={() => setIsActualOpen(false)}
                disabled={actualMutation.isPending}
                className={secondaryButtonClass}
              >
                Cancel
              </button>
              <button type="submit" disabled={actualMutation.isPending} className={primaryButtonClass}>
                {actualMutation.isPending ? 'Saving...' : 'Save Actual'}
              </button>
            </FormActions>
          </form>
        )}
      </Modal>

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Forecast"
        message={
          <>
            The {deleteTarget ? ForecastPeriodLabels[deleteTarget.period].toLowerCase() : ''} forecast for{' '}
            <span className="font-medium text-gray-900">{deleteTarget?.productName}</span> on{' '}
            {formatForecastDate(deleteTarget?.forecastDate)} will be permanently deleted. This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        isPending={deleteMutation.isPending}
      />
    </div>
  );
};

export default DemandForecastingScreen;
